import { NumberField as BaseNumberField } from '@base-ui-components/react/number-field';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import * as React from 'react';

/**
 * This component is a placeholder for FormControl to correctly set the shrink label state on SSR.
 */
function SSRInitialFilled(_: BaseNumberField.Root.Props) {
  return null;
}
SSRInitialFilled.muiName = 'Input';

export const NumberField = ({
  id: idProp,
  label,
  helperText,
  endAdornment,
  allowWheelScrub = true,
  showSpinners = true,
  ...other
}: BaseNumberField.Root.Props &
  React.ComponentProps<typeof FormControl> & {
    label?: string;
    helperText?: string;
    endAdornment?: React.ReactNode;
    showSpinners?: boolean;
  }) => {
  let id = React.useId();
  if (idProp) {
    id = idProp;
  }

  return (
    <BaseNumberField.Root
      allowWheelScrub={allowWheelScrub}
      {...other}
      render={(props, state) => (
        <FormControl
          size={other.size}
          ref={props.ref}
          disabled={state.disabled}
          required={state.required}
          error={other.error}
          variant="outlined"
          sx={other.sx}
        >
          {props.children}
        </FormControl>
      )}
    >
      <SSRInitialFilled {...other} />
      <InputLabel htmlFor={id}>{label}</InputLabel>
      <BaseNumberField.Input
        id={id}
        render={(props, state) => (
          <OutlinedInput
            label={label}
            inputRef={props.ref}
            value={state.inputValue}
            onBlur={props.onBlur}
            onChange={props.onChange}
            onKeyUp={props.onKeyUp}
            onKeyDown={props.onKeyDown}
            onFocus={props.onFocus}
            slotProps={{
              input: props,
            }}
            endAdornment={
              <>
                {endAdornment && (
                  <InputAdornment position="end" sx={{ mr: 1 }}>
                    {endAdornment}
                  </InputAdornment>
                )}
                {showSpinners && (
                  <InputAdornment
                    position="end"
                    sx={{
                      flexDirection: 'column',
                      maxHeight: 'unset',
                      alignSelf: 'stretch',
                      borderLeft: '1px solid',
                      borderColor: 'divider',
                      ml: 0,
                      '& button': {
                        py: 0,
                        flex: 1,
                        borderRadius: 0.5,
                      },
                    }}
                  >
                    <BaseNumberField.Increment
                      render={<IconButton size={other.size} aria-label="Increase" />}
                    >
                      <KeyboardArrowUpIcon
                        fontSize={other.size}
                        sx={{ transform: 'translateY(2px)' }}
                      />
                    </BaseNumberField.Increment>

                    <BaseNumberField.Decrement
                      render={<IconButton size={other.size} aria-label="Decrease" />}
                    >
                      <KeyboardArrowDownIcon
                        fontSize={other.size}
                        sx={{ transform: 'translateY(-2px)' }}
                      />
                    </BaseNumberField.Decrement>
                  </InputAdornment>
                )}
              </>
            }
            sx={{ pr: showSpinners ? 0 : undefined }}
          />
        )}
      />
      {helperText && (
        <FormHelperText sx={{ ml: 0, '&:empty': { mt: 0 } }}>{helperText}</FormHelperText>
      )}
    </BaseNumberField.Root>
  );
};
